import { Model } from '@contember/schema';
import { DefaultNamingConventions, isOwningRelation, isRelation } from '@contember/schema-utils';
import { camelCase } from 'change-case';
import pluralize from 'pluralize';
import { SchemaBuilder } from './builder/SchemaBuilder';
import { SchemaPartsFactory } from './builder/SchemaPartsFactory';
var RelationType = Model.RelationType;
const name = {
    localeEntity: 'Locale',
    codeField: 'code',
    labelField: 'label',
    localesRelation: 'locales',
    localeRelation: 'locale',
    rootRelation: 'root',
};
export const applySchemaExtensions = (schema, schemaExtensions) => {
    const namingConventions = new DefaultNamingConventions();
    const schemaParts = new SchemaPartsFactory(namingConventions);
    const schemaBuilder = new SchemaBuilder(schema, schemaParts, namingConventions, {});
    const translatableEntities = Object.entries(schemaExtensions.translatable);
    let builder = schemaBuilder;
    if (!translatableEntities.length)
        return builder.schema;
    builder = createRootLocaleEntity(builder);
    for (const [entityName, fields] of translatableEntities) {
        const localizationEntityName = `${entityName}${name.localeEntity}`;
        const relationFieldName = pluralize.plural(camelCase(entityName));
        if (!builder.schema.model.entities[entityName]) {
            continue;
        }
        builder = createLocaleEntity(builder, { entityName, localizationEntityName, relationFieldName });
        for (const field of fields) {
            const entity = schemaBuilder.schema.model.entities[entityName];
            const entityField = entity.fields[field];
            if (!entityField) {
                continue;
            }
            if (!isRelation(entityField)) {
                builder = moveField(builder, {
                    entityName,
                    field,
                    newEntityName: localizationEntityName,
                    newField: {
                        ...entityField,
                        columnName: namingConventions.getColumnName(field),
                    },
                });
                continue;
            }
            const targetEntity = schemaBuilder.schema.model.entities[entityField.target];
            builder = builder.addRelation(localizationEntityName, entityField);
            if (isOwningRelation(entityField)) {
                builder = moveOwningRelation(builder, {
                    targetEntity,
                    entityName,
                    field,
                    localizationEntityName,
                    entityField,
                });
                continue;
            }
            builder = moveInverseRelation(builder, {
                entityName,
                field,
                targetEntity,
                localizationEntityName,
                entityField,
            });
        }
    }
    return builder.schema;
};
// TODO: better typing
const createRootLocaleEntity = (builder) => {
    const namingConventions = new DefaultNamingConventions();
    return builder
        .addEntity({ name: name.localeEntity })
        .addField(name.localeEntity, {
        name: name.codeField,
        columnName: namingConventions.getColumnName(name.codeField),
        type: Model.ColumnType.String,
        columnType: 'text',
        nullable: false,
    })
        .addField(name.localeEntity, {
        name: name.labelField,
        columnName: namingConventions.getColumnName(name.labelField),
        type: Model.ColumnType.String,
        columnType: 'text',
        nullable: false,
    })
        .createUnique(name.localeEntity, {
        fields: [name.codeField],
    });
};
const createLocaleEntity = (builder, { entityName, localizationEntityName, relationFieldName }) => {
    return builder
        .addEntity({ name: localizationEntityName })
        .addRelation(entityName, {
        name: name.localesRelation,
        type: RelationType.OneHasMany,
        target: localizationEntityName,
        ownedBy: name.rootRelation,
    })
        .addRelation(localizationEntityName, {
        name: name.rootRelation,
        type: RelationType.ManyHasOne,
        target: entityName,
        inversedBy: name.localesRelation,
    })
        .addRelation(localizationEntityName, {
        name: name.localeRelation,
        type: RelationType.ManyHasOne,
        target: name.localeEntity,
        inversedBy: relationFieldName,
    })
        .addRelation(name.localeEntity, {
        name: relationFieldName,
        type: RelationType.OneHasMany,
        target: localizationEntityName,
        ownedBy: name.localeRelation,
    })
        .createUnique(localizationEntityName, {
        fields: ['root', name.localeRelation],
    });
};
const moveField = (builder, { entityName, field, newEntityName, newField }) => {
    return builder.addField(newEntityName, newField).removeField(entityName, field);
};
const moveOwningRelation = (builder, { targetEntity, entityName, field, localizationEntityName, entityField }) => {
    if (!entityField.inversedBy)
        return builder.removeField(entityName, field);
    const targetField = targetEntity.fields[entityField.inversedBy];
    const newLocaleRelation = {
        ...targetField,
        target: localizationEntityName,
    };
    return builder
        .removeField(entityField.target, entityField.inversedBy)
        .removeField(entityName, targetField.ownedBy)
        .addRelation(entityField.target, newLocaleRelation);
};
const moveInverseRelation = (builder, { entityName, field, targetEntity, localizationEntityName, entityField }) => {
    if (!entityField.ownedBy)
        return builder.removeField(entityName, field);
    const targetField = targetEntity.fields[entityField.ownedBy];
    const newLocaleRelation = {
        ...targetField,
        target: localizationEntityName,
    };
    if (builder.schema.model.entities[entityField.target].fields[entityField.ownedBy]) {
        return builder.removeField(entityField.target, entityField.ownedBy).addRelation(entityField.target, newLocaleRelation);
    }
    return builder.removeField(entityName, field).addRelation(entityField.target, newLocaleRelation);
};
//# sourceMappingURL=applySchemaExtensions.js.map